import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/sportsData";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">文章不存在</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      "@type": "Organization",
      name: "极速体育",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://www.jisusports.com/" },
      { "@type": "ListItem", position: 2, name: `${article.categoryName}资讯`, item: `https://www.jisusports.com/category/${article.category}` },
      { "@type": "ListItem", position: 3, name: article.title, item: `https://www.jisusports.com/article/${article.id}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 极速体育`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},极速体育,体育资讯`}
        canonicalUrl={`https://www.jisusports.com/article/${article.id}`}
        ogType="article"
        structuredData={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: `${article.categoryName}资讯`, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                {article.categoryName}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <div className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  <span>{article.date} {article.time}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  <span>{article.readCount} 阅读</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  <span>{article.commentCount} 评论</span>
                </div>
              </div>
            </div>

            <div className="mb-8 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[300px] md:h-[450px] object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-10 article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 mb-10 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-6 border-t border-background-200/70">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1.5 px-4 py-2 bg-background-100 hover:bg-background-200/70 rounded-full text-sm text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-thumb-up-line"></i>
                  点赞
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 bg-background-100 hover:bg-background-200/70 rounded-full text-sm text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-star-line"></i>
                  收藏
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 bg-background-100 hover:bg-background-200/70 rounded-full text-sm text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12">
                <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer group"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
    </>
  );
}